import { useState, useEffect, useCallback, useMemo } from 'react'
import { Logger } from '../utils/logger'
import { useIdentity } from '@vp/ubik-context'
import { CLAIM_CIMPRESS_INTERNAL, FALLBACK_ACCOUNT_ID } from '../constants/config'
import { useTenantConfig } from '../context/TenantConfigContext'
import { resetTracking } from '../tracking/misc'

interface UseAuthReturn {
  auth: ReturnType<typeof useIdentity>['auth']
  isSignedIn: boolean
  identity: ReturnType<typeof useIdentity>['identity']
  profile: ProfileData
  token: string
  shopperId: string
  canonicalId: string
  signIn: () => void
  signUp: () => void
  signOut: (e: React.MouseEvent<HTMLElement>) => void
}

type ProfileData = Record<string, string | undefined> | undefined

// Module-level cache: deduplicates concurrent getUserProfile calls across all
// useAuth instances so only one network request is made per token value.
let profileCache: { token: string; promise: Promise<ProfileData> } | null = null

async function getUserProfile(token: string, isInternalUser: boolean, accountId: string): Promise<ProfileData> {
  if (!token) {
    throw new Error('token was undefined')
  }

  // Return the cached promise if a fetch for this token is already in-flight or resolved.
  if (profileCache && profileCache.token === token) {
    return profileCache.promise
  }

  const promise = (async (): Promise<ProfileData> => {
    try {
      const url = isInternalUser ? `https://profile.cimpress.io/v1/profile/me` : `https://profile.cimpress.io/v1/${accountId}/profile/me`

      const response = await fetch(url, {
        headers: {
          Authorization: `${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.statusText}`)
      }

      return (await response.json()) as ProfileData
    } catch (error) {
      Logger.instance.error('Error fetching user profile', {
        experience: 'Bookends flyout',
        error,
      })
      return undefined
    }
  })()

  profileCache = { token, promise }
  return promise
}

export const useAuth = (): UseAuthReturn => {
  const { auth, identity, isSignedIn } = useIdentity()
  const tenantConfig = useTenantConfig()
  const accountId = tenantConfig?.accountId || FALLBACK_ACCOUNT_ID
  const [profile, setProfile] = useState<ProfileData>(undefined)
  const [token, setToken] = useState<string>('')
  const [shopperId, setShopperId] = useState<string>('')

  // Extract token and shopperId from auth.
  // Does NOT depend on `profile` — the old dep caused a feedback loop:
  // profile change → setToken → token change → fetch profile → profile change → …
  useEffect(() => {
    if (auth && 'getAuthorizationHeader' in auth) {
      setToken(auth.getAuthorizationHeader())
      if (auth.isSignedIn()) {
        setShopperId(auth.getProfile()['https://claims.cimpress.io/canonical_id']!)
      }
    }
  }, [auth])

  // Fetch profile when token changes. The module-level profileCache inside
  // getUserProfile deduplicates across all useAuth instances, and React's
  // useState bails out on same-value token strings — so no extra guard needed.
  useEffect(() => {
    if (!token) return

    const isInternalUser = !!profile?.[CLAIM_CIMPRESS_INTERNAL]
    getUserProfile(token, isInternalUser, accountId).then(setProfile)
  }, [token])

  // Listen for identity changes. Registered once per `auth` instance (no
  // accumulating listeners). On change, invalidate the dedup ref and set the
  // new token so the fetch effect picks it up.
  useEffect(() => {
    if (!auth?.onUserIdentityUpdate) return

    const unsubscribe = auth.onUserIdentityUpdate((newIdentity) => {
      if (!newIdentity) return

      const newToken = (newIdentity as Record<string, unknown>)['authorizationHeader'] as string | undefined

      // setToken bails out if the value hasn't changed (React useState).
      // When it IS a new token, the token effect fires and getUserProfile
      // naturally misses the module cache — no explicit invalidation needed.
      if (newToken) {
        setToken(newToken)
      }
    })

    return typeof unsubscribe === 'function' ? unsubscribe : undefined
  }, [auth])

  const signOut = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      resetTracking()
      auth?.signOut()
    },
    [auth],
  )

  const signIn = useCallback(() => {
    if (!isSignedIn) auth?.signIn([], [], { trackingCaller: 'header' })
  }, [auth, isSignedIn])

  const signUp = useCallback(() => {
    if (!isSignedIn) auth?.signIn([], [], { trackingCaller: 'header', navHint: 'signUp' })
  }, [auth, isSignedIn])

  const canonicalId = useMemo(() => auth?.getCanonicalId?.() || '', [auth])

  return {
    auth,
    isSignedIn,
    identity,
    profile,
    token,
    shopperId,
    signIn,
    signUp,
    signOut,
    canonicalId,
  }
}
